<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import useFlowNavigation from '@Use/useFlowNavigation';
import { FlowLayout } from '@Shared';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { getNextAuthFlowApi, getStoredMfeRoute, getAuthFlowStatusApi } from '@Api/authFlow.api'
import { AuthFlowStatus } from '@Types/AuthFlowState.interface';
import { useAuthFlowStore } from '@Stores/authFlow.store'
import { resolveDeviceAuthEnabled } from '@Use/useDeviceAuthFlag';
import useStatusPolling, { type PollResult } from '@Use/useStatusPolling';
import { SessionExpiredError, redirectToSessionExpired } from '@Utils/sessionExpired';
import { setCurrentMfeRoute } from 'host/mfeBridge'

// Stores / base data
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
// const authSessionStore = useAuthSessionStore();
const {advanceFlow} = useFlowNavigation();
const companyImageBaseUrl = useCompanyImageBaseUrl();

const authFlowStore = useAuthFlowStore();
const currentMfeRoute = ref<string|null>(null);
const router = useRouter();

// Actions
let flowCompleteDetected = false;

// Lifecycle hooks
onMounted(async () => {
    await startPollingForIdvStatus();
});

const startPollingForIdvStatus = async () => {
    try {
        currentMfeRoute.value = await getStoredMfeRoute();
        var redirectRoute = router.resolve(currentMfeRoute.value);
        setCurrentMfeRoute(redirectRoute);
    } catch (error) {
        if (error instanceof SessionExpiredError) {
            redirectToSessionExpired();
            return;
        }
        // The stored route only keeps the host URL in sync; the status poll can run without it.
        console.error('[Auth MFE] GetCompletionRedirectUrl failed:', error);
    }
    polling.start();
};

// One poll of the IDV result. A 404 from either API throws SessionExpiredError, which
// useStatusPolling turns into the session-expired screen.
const checkIdvStatus = async (): Promise<PollResult> => {
    const deviceAuthEnabled = await resolveDeviceAuthEnabled();
    const useLegacyAuth = !deviceAuthEnabled;

    // Use GetAuthFlowStatus only until first Complete is detected
    if (!useLegacyAuth && !flowCompleteDetected) {
        const flowState = await getAuthFlowStatusApi();
        if (flowState && flowState.status === AuthFlowStatus.Complete) {
            flowCompleteDetected = true;
            authFlowStore._clearFlowSequence();
            advanceFlow();
            return 'continue';
        }

        // GetAuthFlowStatus failed (500) — fall back to GetNextAuthFlow
        if (!flowState) {
            var fallbackRoutes = await getNextAuthFlowApi();
            if (fallbackRoutes && fallbackRoutes.length > 0) {
                authFlowStore._clearFlowSequence();
                authFlowStore.addToAuthFlow(fallbackRoutes);
                advanceFlow();
                return 'stop';
            }
            router.replace({ name: 'identity-complete' });
            return 'stop';
        }

        return 'continue';
    }

    // Legacy path OR post-Complete path: only GetNextAuthFlow
    var newRoutes = await getNextAuthFlowApi();
    if (newRoutes == undefined) {
        return 'failed';
    }

    if (newRoutes.length > 0) {
        authFlowStore._clearFlowSequence();
        authFlowStore.addToAuthFlow(newRoutes);
        if (flowCompleteDetected) {
            // Post-Complete: stay on page, reset flag, poll with GetAuthFlowStatus
            flowCompleteDetected = false;
            return 'continue';
        }
        // Legacy: advance to the returned route
        advanceFlow();
        return 'stop';
    }

    authFlowStore._clearFlowSequence();
    advanceFlow();
    return 'stop';
}

// Stops itself on unmount.
const polling = useStatusPolling(checkIdvStatus);



</script>
<template>
    <flow-layout
        suppress-back-to-dashboard
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">
        <app-brand-loader class="loader">
            Verifying your information...
            <template #caption>
                <p>
                This may take a few seconds, do not refresh this page.
                </p>
                <p>
                    If you used a QR code, your computer browser will automatically continue once the results are in.
                </p>
            </template>
        </app-brand-loader>

    </flow-layout>
</template>
    

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

::v-deep(.focus-message-container) {
    position: absolute;
}
</style>
